import { Avatar, Box, Button, Divider, Menu, MenuItem, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutIcon from "@mui/icons-material/Logout";
import { roleLabel, type SessionUser } from "@xop-any-time/shared";
import { Network, UserRound, UsersRound } from "lucide-react";
import { useId, useState, type JSX, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/AuthProvider.js";
import { ThemeToggleSegment } from "../../components/ui/theme-toggle-segment.js";

/**
 * The top-right account control — a close port of xop-edge-staff's
 * `components/ProfileMenu.tsx` + `components/UserAvatar.tsx`.
 *
 * Same trigger (a pill Button: avatar, name, chevron that rotates when
 * open), same 256px menu with the name / email / role block at the top and
 * a red "Sign out" item at the bottom, same MUI `Menu` for click-outside,
 * Escape, focus management and ARIA.
 *
 * DROPPED from the source, because none of it has a counterpart here: the
 * presence dot and availability picker (there is no realtime channel for
 * resellers), the avatar image fetch (`GET /api/users/:id/avatar` is a
 * staff endpoint, so this renders initials only), and "System admin" (a
 * reseller has no admin surface).
 *
 * ADDED here, and absent from the source (spec D §7.2):
 *
 *   • **A light/dark toggle.** The source has none — the command center is
 *     light-only. This portal is not, and the account menu is where the
 *     preference belongs once the sign-in screen is behind you.
 *   • **Your profile / Your team / Reseller network.**
 *
 * WHY "YOUR TEAM" IS HIDDEN FOR A SALESPERSON AND NOT MERELY DISABLED: a
 * disabled control advertises a capability, and the honest reading of this
 * one is that the page is not theirs. Hiding it is a UI courtesy and NOTHING
 * MORE — `user.role` here comes from the session cookie, which is minted at
 * sign-in and stale for hours after a demotion. The actual refusal is
 * crm-api's, from the membership it re-resolves per request (spec D §6.1).
 * Never move an authorisation decision into this file.
 */

/** Up-to-two-letter initials from a display name — the source's own
 * `initialsFor`, unchanged. */
export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function ProfileMenu({ user }: { user: SessionUser }): JSX.Element {
  const { logout } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const menuId = useId();
  const close = (): void => setAnchorEl(null);
  const go = (to: string) => (): void => {
    close();
    navigate(to);
  };

  // The source colours the initials avatar deterministically from the user
  // id so a person looks the same everywhere. A reseller session has no CRM
  // user id, so seed from the RESELLER id instead — everyone at one reseller
  // then shares its colour, which is the useful grouping here anyway.
  const sys = theme.palette.system as Partial<Record<string, string>> | undefined;
  const palette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.info.main,
    sys?.crm,
    sys?.sales,
  ].filter((c): c is string => typeof c === "string");
  const bg = palette[Math.abs(user.resellerId) % palette.length] ?? theme.palette.primary.main;

  return (
    <>
      <Button
        type="button"
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account — ${user.name}`}
        data-testid="profile-menu-trigger"
        {...(open ? { "aria-controls": menuId } : {})}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 14,
              color: "text.disabled",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 150ms",
            }}
          />
        }
        sx={{
          borderRadius: 999,
          pl: 0.5,
          pr: 1,
          py: 0.5,
          color: "text.primary",
          textTransform: "none",
          fontWeight: 500,
          "&:hover": { bgcolor: "action.hover" },
          ...(open ? { bgcolor: "action.hover" } : {}),
        }}
      >
        <Avatar
          alt=""
          sx={{
            width: 34,
            height: 34,
            bgcolor: bg,
            color: theme.palette.getContrastText(bg),
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {initialsFor(user.name)}
        </Avatar>
        <Box
          component="span"
          sx={{
            display: { xs: "none", md: "inline" },
            ml: 1,
            whiteSpace: "nowrap",
            fontSize: 14,
          }}
        >
          {user.name}
        </Box>
      </Button>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 256, mt: 1 } } }}
        MenuListProps={{ "aria-label": "Account menu" }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
            {user.name}
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 12,
              color: "text.disabled",
            }}
          >
            {user.email}
          </Typography>
          {/* The reseller company, not just the role. A reseller-of-reseller
              sees a SUBTREE, so "which company am I acting for" is the
              question this block has to answer — the staff version has no
              equivalent because staff act for the tenant. */}
          <Typography
            sx={{
              mt: 0.75,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 12,
              fontWeight: 500,
              color: "text.secondary",
            }}
          >
            {user.resellerName}
          </Typography>
          <Box
            component="span"
            sx={{
              mt: 1,
              display: "inline-flex",
              borderRadius: 999,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
              color: "primary.main",
              px: 1,
              py: 0.25,
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {/* The real role — a technician was shown "Salesperson" by the
                old owner/else branch, and a custom role has only its label. */}
            {roleLabel(user.role, user.roleLabel)}
          </Box>
        </Box>

        {/* Appearance. A row rather than a menu item: the segmented control
            is a two-state choice, and wrapping it in a `MenuItem` would make
            clicking either half also close the menu — so you could not see
            what you had just picked. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            py: 1.25,
          }}
        >
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Appearance</Typography>
          <ThemeToggleSegment surface="themed" />
        </Box>

        <Divider sx={{ my: 0 }} />

        <MenuItem onClick={go("/profile")} sx={{ fontSize: 14 }} data-testid="menu-profile">
          <Box sx={{ display: "flex", mr: 1, color: "text.secondary" }}>
            <UserRound size={16} />
          </Box>
          Your profile
        </MenuItem>

        {/* See the header comment: hidden, not disabled, and NOT a security
            control. crm-api refuses the write regardless of what is rendered. */}
        {user.role === "owner" && (
          <MenuItem onClick={go("/team")} sx={{ fontSize: 14 }} data-testid="menu-team">
            <Box sx={{ display: "flex", mr: 1, color: "text.secondary" }}>
              <UsersRound size={16} />
            </Box>
            Your team
          </MenuItem>
        )}

        <MenuItem onClick={go("/network")} sx={{ fontSize: 14 }} data-testid="menu-network">
          <Box sx={{ display: "flex", mr: 1, color: "text.secondary" }}>
            <Network size={16} />
          </Box>
          Reseller network
        </MenuItem>

        <Divider sx={{ my: 0 }} />

        <MenuItem
          onClick={() => {
            close();
            void logout();
          }}
          sx={{ fontSize: 14, color: "error.main" }}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
